import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, PenLine, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const Chip = ({ label, onClick, disabled, testId, strong }) => (
  <button type="button" onClick={onClick} disabled={disabled} data-testid={testId}
    className={`px-3 py-2 rounded-full border text-sm transition-colors disabled:opacity-50 ${strong ? "border-[#FD0F10] text-white bg-[#FD0F10]/10" : "border-[#2A2E39] text-gray-200 hover:border-[#FD0F10] hover:text-white"}`}>
    {label}
  </button>
);

export const ServicePicker = ({ title, onPicked, switching = false }) => {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(null);
  const [other, setOther] = useState(false);
  const [custom, setCustom] = useState("");
  const [picks, setPicks] = useState(null);
  const [limit, setLimit] = useState(null);

  useEffect(() => { api.get("/local-service").then(r => setState(r.data)).catch(() => {}); }, []);

  const choose = async (service) => {
    if (!service.trim() || busy) return;
    setBusy(service);
    try {
      const { data } = await api.post("/local-service", { service: service.trim() });
      setState(data);
      toast.success(`Your 7-day ${service.trim().toLowerCase()} sprint is ready.`);
      onPicked?.(data.service);
    } catch (e) {
      if (e?.response?.status === 402) setLimit(e.response.data?.detail);
      else toast.error(e?.response?.data?.detail || "Couldn't save that. Try again.");
    } finally { setBusy(null); }
  };

  const helpMe = async () => {
    setBusy("suggest");
    try { setPicks((await api.post("/local-service/suggest")).data.picks); }
    catch { toast.error("Couldn't get suggestions. Try again."); }
    finally { setBusy(null); }
  };

  if (!state) return null;
  const blocked = limit || (switching && !state.can_switch ? state.limit_message : null);
  if (blocked) {
    return (
      <div className="p-4 rounded-lg border border-[#FD0F10]/40 bg-[#FD0F10]/5" data-testid="service-switch-limit">
        <p className="text-sm text-white font-bold">{blocked}</p>
        <Link to="/pro" className="mt-3 inline-block hero-accent text-white font-bold px-5 py-2.5 rounded text-sm" data-testid="service-switch-upgrade">Upgrade to Pro</Link>
      </div>
    );
  }
  const options = state.services.filter(s => s.toLowerCase() !== (state.service || "").toLowerCase());
  return (
    <div className="p-4 rounded-lg border border-[#2A2E39] bg-[#14161B]" data-testid="service-picker">
      <div className="eyebrow text-[#FD0F10]">{title || "WHICH SERVICE DO YOU WANT TO OFFER?"}</div>
      {busy && busy !== "suggest" ? (
        <p className="mt-3 text-sm text-gray-300 flex items-center gap-2" data-testid="service-building"><Loader2 size={15} className="animate-spin" /> Building your 7-day {busy.toLowerCase()} plan…</p>
      ) : (
        <>
          {picks && (
            <div className="mt-3 space-y-2" data-testid="service-suggestions">
              <p className="text-xs text-gray-400">Your 3 best fits, based on your quiz answers:</p>
              {picks.map(p => (
                <button key={p.service} type="button" onClick={() => choose(p.service)} data-testid={`service-suggest-${slug(p.service)}`}
                  className="w-full text-left p-3 rounded border border-[#FD0F10]/50 bg-[#FD0F10]/5 hover:bg-[#FD0F10]/10">
                  <div className="text-sm font-bold text-white">{p.service}</div>
                  <div className="text-xs text-gray-300 mt-0.5">{p.why}</div>
                </button>
              ))}
            </div>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            {options.map(s => <Chip key={s} label={s} onClick={() => choose(s)} testId={`service-option-${slug(s)}`} />)}
            <Chip label={<span className="inline-flex items-center gap-1"><PenLine size={13} /> Something else</span>} onClick={() => setOther(true)} testId="service-option-other" strong />
            <Chip label={<span className="inline-flex items-center gap-1">{busy === "suggest" ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} Not sure, help me pick</span>} onClick={helpMe} disabled={busy === "suggest"} testId="service-option-help" strong />
          </div>
          {other && (
            <form onSubmit={e => { e.preventDefault(); choose(custom); }} className="mt-3 flex gap-2">
              <input value={custom} onChange={e => setCustom(e.target.value)} maxLength={60} autoFocus placeholder="e.g. Pool cleaning"
                className="flex-1 min-w-0 bg-[#0B0C0E] border border-[#2A2E39] rounded px-3 py-2 text-sm focus:border-[#FD0F10] outline-none" data-testid="service-custom-input" />
              <button disabled={!custom.trim()} className="hero-accent text-white font-bold px-4 rounded text-sm disabled:opacity-50" data-testid="service-custom-save">Choose</button>
            </form>
          )}
        </>
      )}
    </div>
  );
};
